import { useContext } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { PRODUCT_NAME } from '../../assets/constants/brand';
import { CARD_TYPES, isPhysicalCardType } from '../../assets/constants/cardTypes';
import { TranslationContext } from '../../assets/contexts/translationContext';
import { FreezeIcon } from '../../assets/icons/icons';
import './BankCard.css';

const FROST_TRANSITION = {
  duration: 0.62,
  ease: [0.22, 1, 0.36, 1],
};

const STATUS_TRANSITION = {
  duration: 0.34,
  ease: [0.22, 1, 0.36, 1],
};

const STYLE_TRANSITION = {
  duration: 0.46,
  ease: [0.22, 1, 0.36, 1],
};

const CARD_STYLE_GRADIENTS = {
  sunset: 'linear-gradient(135deg, #f857a6 15%, #ff9a6b 50%, #7ad7f0 85%)',
  aurora: 'linear-gradient(135deg, #c56cf0 15%, #ff8fb1 50%, #ffd86b 85%)',
  mint: 'linear-gradient(135deg, #43e97b 15%, #3ed9a8 50%, #38c9d6 85%)',
  lagoon: 'linear-gradient(135deg, #7367f0 15%, #5b8def 50%, #34e0c9 85%)',
};

const FROST_VARIANTS = {
  active: {
    opacity: 0,
    filter: 'saturate(0.9)',
  },
  frozen: {
    opacity: 1,
    filter: 'saturate(1.05)',
  },
};

const STATUS_VARIANTS = {
  active: {
    opacity: 0,
    x: 8,
    scale: 0.96,
  },
  frozen: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
};

function getCardTypeLabel(cardType, cardCopy) {
  if (isPhysicalCardType(cardType)) {
    return cardCopy.physical;
  }

  return cardCopy.virtual;
}

function CardHeader({ cardTypeLabel, showCardType }) {
  return (
    <div className="bank-card__header">
      {showCardType ? (
        <span className="bank-card__type">{cardTypeLabel}</span>
      ) : (
        <span className="bank-card__header-spacer" aria-hidden="true" />
      )}
      <span className="bank-card__brand bank-card__brand--issuer">{PRODUCT_NAME}</span>
    </div>
  );
}

function FrozenStatus({ active, label, transition, animated = true }) {
  if (!animated) {
    return (
      <span className="bank-card__frozen-status" aria-hidden={!active}>
        <FreezeIcon className="bank-card__frozen-icon" />
        <span>{label}</span>
      </span>
    );
  }

  return (
    <motion.span
      className="bank-card__frozen-status"
      initial={false}
      animate={active ? 'frozen' : 'active'}
      variants={STATUS_VARIANTS}
      transition={transition}
      aria-hidden={!active}
    >
      <FreezeIcon className="bank-card__frozen-icon" />
      <span>{label}</span>
    </motion.span>
  );
}

function PendingStatus({ active, label, transition, animated = true }) {
  if (!animated) {
    if (!active) {
      return null;
    }

    return (
      <span className="bank-card__pending-status" aria-hidden={!active}>
        <span className="bank-card__pending-dot" aria-hidden="true" />
        <span>{label}</span>
      </span>
    );
  }

  return (
    <motion.span
      className="bank-card__pending-status"
      initial={false}
      animate={{
        opacity: active ? 1 : 0,
        x: active ? 0 : 8,
        scale: active ? 1 : 0.96,
      }}
      transition={transition}
      aria-hidden={!active}
    >
      <span className="bank-card__pending-dot" aria-hidden="true" />
      <span>{label}</span>
    </motion.span>
  );
}

function BlockedStatus({ label }) {
  return (
    <span className="bank-card__blocked-status">
      <span className="bank-card__blocked-dot" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function ActivatingStatus({ label }) {
  return (
    <span className="bank-card__activating-status">
      <span className="bank-card__activating-dot" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function CardDetails({
  balance,
  detailsText,
  heldBalance,
  heldLabel,
  label,
  withSideStatus = false,
}) {
  if (!label && !balance && !detailsText && !heldBalance) return null;

  return (
    <div className={`bank-card__details${withSideStatus ? ' bank-card__details--with-side-status' : ''}`}>
      {label && <span className="bank-card__label">{label}</span>}
      {detailsText ? (
        <span className="bank-card__details-text">{detailsText}</span>
      ) : (
        <>
          {balance && <span className="bank-card__balance">{balance}</span>}
          {heldBalance && (
            <span className="bank-card__held-balance">
              <span className="bank-card__held-label">{heldLabel}</span>
              <span>{heldBalance}</span>
            </span>
          )}
        </>
      )}
    </div>
  );
}

function BankCard({
  cardStyle,
  cardType = CARD_TYPES.VIRTUAL,
  showCardType = false,
  size = 'preview',
  lastFour,
  pan,
  label,
  balance,
  detailsText,
  heldBalance,
  frozen = false,
  pending = false,
  blocked = false,
  activating = false,
  animateFrozen = true,
  animatePending = true,
  className,
  ...rest
}) {
  const { card } = useContext(TranslationContext);
  const shouldReduceMotion = useReducedMotion();
  const animationState = frozen ? 'frozen' : 'active';
  const frostTransition = shouldReduceMotion ? { duration: 0 } : FROST_TRANSITION;
  const statusTransition = shouldReduceMotion ? { duration: 0 } : STATUS_TRANSITION;
  const styleTransition = shouldReduceMotion ? { duration: 0 } : STYLE_TRANSITION;
  const isBlocked = blocked && !frozen;
  const isActivating = activating && !frozen && !isBlocked;
  const isPending = pending && !frozen && !isBlocked && !isActivating;
  const hasNumber = Boolean(pan || lastFour);
  const isStatusOnly = !hasNumber && (frozen || isPending || isBlocked || isActivating);
  const classes = [
    'bank-card',
    `bank-card--${cardStyle}`,
    `bank-card--size-${size}`,
    frozen ? 'bank-card--frozen' : null,
    isBlocked ? 'bank-card--blocked' : null,
    isActivating ? 'bank-card--activating' : null,
    isPending ? 'bank-card--pending' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const number = pan || (lastFour ? `•••• ${lastFour}` : null);
  const cardTypeLabel = getCardTypeLabel(cardType, card);
  const cardGradient = CARD_STYLE_GRADIENTS[cardStyle] ?? CARD_STYLE_GRADIENTS.sunset;

  return (
    <motion.article
      className={classes}
      initial={false}
      {...rest}
    >
      <span className="bank-card__visual-layers" aria-hidden="true">
        <motion.span
          className={`bank-card__style-layer bank-card__style-layer--${cardStyle}`}
          initial={false}
          animate={{ background: cardGradient }}
          transition={styleTransition}
          data-testid="bank-card-style-layer"
        />
        {animateFrozen ? (
          <motion.span
            className="bank-card__frost-layer"
            initial={false}
            animate={animationState}
            variants={FROST_VARIANTS}
            transition={frostTransition}
            data-testid="bank-card-frost-layer"
          />
        ) : (
          <span
            className="bank-card__frost-layer"
            data-testid="bank-card-frost-layer"
          />
        )}
        {animatePending ? (
          <motion.span
            className="bank-card__pending-layer"
            initial={false}
            animate={{ opacity: isPending ? 1 : 0 }}
            transition={statusTransition}
          />
        ) : (
          <span
            className="bank-card__pending-layer"
            style={{ opacity: isPending ? 1 : 0 }}
          />
        )}
        {isBlocked && (
          <span className="bank-card__blocked-layer" />
        )}
        {isActivating && (
          <span className="bank-card__activating-layer" />
        )}
      </span>
      <CardHeader cardTypeLabel={cardTypeLabel} showCardType={showCardType} />
      <CardDetails
        label={label}
        balance={balance}
        detailsText={detailsText}
        heldBalance={heldBalance}
        heldLabel={card.holded}
        withSideStatus={isStatusOnly}
      />
      {(number || frozen || isPending || isBlocked || isActivating) && (
        <div className={`bank-card__footer${isStatusOnly ? ' bank-card__footer--status-only' : ''}`}>
          {number && (
            <span className="bank-card__number">{number}</span>
          )}
          {frozen ? (
            <FrozenStatus
              active={frozen}
              label={card.frozen}
              animated={animateFrozen}
              transition={statusTransition}
            />
          ) : isBlocked ? (
            <BlockedStatus label={card.blocked} />
          ) : isActivating ? (
            <ActivatingStatus label={card.activating} />
          ) : (
            <PendingStatus
              active={isPending}
              label={card.pending}
              animated={animatePending}
              transition={statusTransition}
            />
          )}
        </div>
      )}
    </motion.article>
  );
}

export default BankCard;
